import { supabase } from '@/lib/supabase';
import {
  uploadToSeaweedFS,
  deleteFile as seaweedDelete,
  getFileBytes as seaweedGetBytes,
  formatFileSize,
} from '@/lib/seaweedfs';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 49. FILE STORAGE
// Zifek Core ne dépend JAMAIS d'un stockage local spécifique. On expose une
// abstraction « Storage Provider » :
//
//   upload() · download() · delete() · getUrl()
//
// Les modules appellent ces fonctions SANS connaître le fournisseur
// (SeaweedFS, S3 compatible, stockage cloud, ou un stockage futur).
// Le fournisseur par défaut est configurable dans core_storage_providers.
// ─────────────────────────────────────────────────────────────

export interface StorageObject {
  key: string;
  url: string;
  provider: string;
  size?: number;
}

export interface UploadOptions {
  contentType?: string;
  fileName?: string;
}

/** Contrat que tout fournisseur de stockage doit respecter. */
export interface StorageProvider {
  key: string;
  label: string;
  kind: string;
  upload(file: File | Blob, folder: string, opts?: UploadOptions): Promise<StorageObject>;
  download(key: string): Promise<ArrayBuffer>;
  remove(key: string): Promise<boolean>;
  getUrl(key: string, expiresIn?: number): Promise<string>;
}

const SEAWEEDFS_ENDPOINT = 'https://seaweedfs-oriq.srv1134875.hstgr.cloud';
const SEAWEEDFS_BUCKET = 'product-media';

function stripSeaweedPrefix(url: string): string {
  const marker = `/${SEAWEEDFS_BUCKET}/`;
  const idx = url.indexOf(marker);
  return idx >= 0 ? url.slice(idx + marker.length) : url;
}

function sanitizeName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : 'bin';
  const clean = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'fichier';
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 6)
    : Math.random().toString(36).slice(2, 8);
  return `${clean}-${rand}.${ext}`;
}

/** Fournisseur SeaweedFS (S3-compatible) — implémentation par défaut. */
const seaweedfsProvider: StorageProvider = {
  key: 'seaweedfs',
  label: 'SeaweedFS (S3-compatible)',
  kind: 's3',
  async upload(file, folder, opts) {
    const fileName = opts?.fileName || (file as File).name || 'fichier';
    const contentType = opts?.contentType || file.type || 'application/octet-stream';
    const bytes = new Uint8Array(await file.arrayBuffer());
    const url = await uploadToSeaweedFS(bytes, fileName, contentType, folder);
    return { key: stripSeaweedPrefix(url), url, provider: 'seaweedfs', size: file.size };
  },
  async download(key) {
    return seaweedGetBytes(stripSeaweedPrefix(key));
  },
  async remove(key) {
    return seaweedDelete(stripSeaweedPrefix(key));
  },
  async getUrl(key) {
    return `${SEAWEEDFS_ENDPOINT}/${SEAWEEDFS_BUCKET}/${stripSeaweedPrefix(key)}`;
  },
};

/** Fournisseur Readdy Backend Storage (via supabase-js, buckets logiques). */
const readdyProvider: StorageProvider = {
  key: 'readdy',
  label: 'Readdy Backend Storage',
  kind: 'cloud',
  async upload(file, folder, opts) {
    const bucket = 'product-media';
    const fileName = opts?.fileName || (file as File).name || 'fichier';
    const path = `${folder}/${sanitizeName(fileName)}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      contentType: opts?.contentType || file.type || 'application/octet-stream',
      upsert: true,
    });
    if (error) throw error;
    const url = await this.getUrl(path);
    return { key: path, url, provider: 'readdy', size: file.size };
  },
  async download(key) {
    const { data, error } = await supabase.storage.from('product-media').download(key);
    if (error) throw error;
    return data.arrayBuffer();
  },
  async remove(key) {
    const { error } = await supabase.storage.from('product-media').remove([key]);
    return !error;
  },
  async getUrl(key, expiresIn = 3600) {
    const { data, error } = await supabase.storage.from('product-media').createSignedUrl(key, expiresIn);
    if (error || !data?.signedUrl) return '';
    return data.signedUrl;
  },
};

/** Registre des fournisseurs disponibles côté application. */
export const STORAGE_PROVIDERS: Record<string, StorageProvider> = {
  seaweedfs: seaweedfsProvider,
  readdy: readdyProvider,
};

export interface StorageProviderRow {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  provider_key: string;
  label: string | null;
  kind: string | null;
  config: Record<string, unknown> | null;
  is_default: boolean | null;
  is_active: boolean | null;
  priority: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function listStorageProviders(organizationId?: number | null): Promise<StorageProviderRow[]> {
  let query = supabase.from('core_storage_providers').select('*').order('priority', { ascending: true });
  if (organizationId != null) {
    query = query.or(`organization_id.is.null,organization_id.eq.${organizationId}`);
  }
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as StorageProviderRow[];
}

export async function setDefaultStorageProvider(providerRowId: number, organizationId: number | null): Promise<void> {
  // Un seul fournisseur par défaut par périmètre.
  if (organizationId != null) {
    await supabase.from('core_storage_providers').update({ is_default: false }).eq('organization_id', organizationId);
  } else {
    await supabase.from('core_storage_providers').update({ is_default: false }).is('organization_id', null);
  }
  await supabase.from('core_storage_providers').update({ is_default: true, is_active: true, updated_at: new Date().toISOString() }).eq('id', providerRowId);
}

export async function setStorageProviderActive(id: number, active: boolean): Promise<void> {
  const { error } = await supabase
    .from('core_storage_providers')
    .update({ is_active: active, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Résout le fournisseur actif pour une organisation (org → plateforme → défaut). */
export async function resolveStorageProvider(organizationId?: number | null): Promise<StorageProvider> {
  try {
    const rows = await listStorageProviders(organizationId ?? null);
    const orgDefault = rows.find((r) => r.organization_id === organizationId && r.is_default && r.is_active !== false);
    const platformDefault = rows.find((r) => r.organization_id == null && r.is_default && r.is_active !== false);
    const chosen = orgDefault?.provider_key || platformDefault?.provider_key || 'seaweedfs';
    return STORAGE_PROVIDERS[chosen] || seaweedfsProvider;
  } catch {
    return seaweedfsProvider;
  }
}

// ── API publique agnostique du fournisseur ──
// Les modules n'appellent QUE ces fonctions ; ils ignorent le stockage réel.

export async function upload(
  file: File | Blob,
  folder: string,
  opts?: UploadOptions & { organizationId?: number | null },
): Promise<StorageObject> {
  const provider = await resolveStorageProvider(opts?.organizationId ?? null);
  return provider.upload(file, folder, opts);
}

export async function download(key: string, organizationId?: number | null): Promise<ArrayBuffer> {
  const provider = await resolveStorageProvider(organizationId ?? null);
  return provider.download(key);
}

export async function remove(key: string, organizationId?: number | null): Promise<boolean> {
  const provider = await resolveStorageProvider(organizationId ?? null);
  return provider.remove(key);
}

export async function getUrl(key: string, opts?: { expiresIn?: number; organizationId?: number | null }): Promise<string> {
  const provider = await resolveStorageProvider(opts?.organizationId ?? null);
  return provider.getUrl(key, opts?.expiresIn);
}

export { formatFileSize };

export interface StorageStats {
  providerCount: number;
  activeCount: number;
  defaultProvider: string;
}

export async function fetchStorageStats(organizationId?: number | null): Promise<StorageStats> {
  const rows = await listStorageProviders(organizationId ?? null);
  const def = rows.find((r) => r.is_default && r.is_active !== false);
  return {
    providerCount: rows.length,
    activeCount: rows.filter((r) => r.is_active !== false).length,
    defaultProvider: def?.provider_key || 'seaweedfs',
  };
}